import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  School, 
  GraduationCap, 
  BookOpen, 
  Search, 
  MapPin, 
  Filter,
  ArrowRight,
  Building2,
  ClipboardList,
  GitCompare
} from 'lucide-react';
import { admissionsService } from '../firebase/admissions';
import toast from 'react-hot-toast';
import Layout from '../components/layout/Layout';
import ComparisonModal from '../components/admissions/ComparisonModal';
import ComparisonView from '../components/admissions/ComparisonView';
import R2Image from '../components/R2Image';

const Admissions = () => {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  useEffect(() => {
    loadInstitutions();
  }, []);

  useEffect(() => {
    filterInstitutions();
  }, [searchTerm, filterType, filterCity, institutions]);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAllInstitutions({ isActive: true });
      
      if (result.success) {
        setInstitutions(result.institutions || []);
      } else {
        toast.error('Failed to load institutions');
      }
    } catch (error) {
      toast.error('Error loading institutions');
    } finally {
      setLoading(false);
    }
  };

  const filterInstitutions = () => {
    let filtered = [...institutions];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(inst => inst.type === filterType);
    }

    // Filter by city
    if (filterCity !== 'all') {
      filtered = filtered.filter(inst => inst.address?.city === filterCity);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inst => 
        inst.name?.toLowerCase().includes(term) ||
        inst.description?.toLowerCase().includes(term) ||
        inst.address?.city?.toLowerCase().includes(term)
      );
    }

    setFilteredInstitutions(filtered);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'school':
        return <School className="w-6 h-6" />;
      case 'college':
        return <GraduationCap className="w-6 h-6" />;
      case 'coaching':
        return <BookOpen className="w-6 h-6" />;
      default:
        return <Building2 className="w-6 h-6" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school':
        return 'bg-blue-100 text-blue-800';
      case 'college':
        return 'bg-purple-100 text-purple-800';
      case 'coaching':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCities = () => {
    const cities = new Set();
    institutions.forEach(inst => {
      if (inst.address?.city) {
        cities.add(inst.address.city);
      }
    });
    return Array.from(cities).sort();
  };

  const handleCompare = (selectedInstitutions) => {
    setSelectedForComparison(selectedInstitutions);
    setShowComparisonModal(false);
    setShowComparisonView(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="relative mb-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Admissions</h1>
              <p className="text-xl text-gray-600">
                Explore schools, colleges, and coaching centers
              </p>
            </div>
            {/* Action Buttons - Top Right */}
            <div className="absolute top-0 right-0 flex items-center gap-3">
              <button
                onClick={() => setShowComparisonModal(true)}
                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors shadow-md"
              >
                <GitCompare className="w-5 h-5" />
                <span className="hidden sm:inline">Compare</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/my-applications')}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                <ClipboardList className="w-5 h-5" />
                <span className="hidden sm:inline">My Applications</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search institutions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-5 h-5" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="school">Schools</option>
                  <option value="college">Colleges</option>
                  <option value="coaching">Coachings</option>
                </select>
              </div>

              {/* City Filter */}
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Cities</option>
                {getCities().map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-gray-600">
              Found <span className="font-semibold">{filteredInstitutions.length}</span> institutions
            </p>
          </div>

          {/* Institutions Grid */}
          {filteredInstitutions.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No institutions found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInstitutions.map((institution) => (
                <motion.div
                  key={institution.id}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => navigate(`/admissions/${institution.id}`)}
                >
                  {/* Image */}
                  <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 relative">
                    {institution.profilePicture ? (
                      <R2Image
                        fileKey={institution.profilePicture}
                        alt={institution.name}
                        className="w-full h-full object-cover"
                      />
                    ) : institution.logo ? (
                      <R2Image
                        fileKey={institution.logo}
                        alt={institution.name}
                        className="w-full h-full object-cover"
                      />
                    ) : null}
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full ${getTypeColor(institution.type)} flex items-center gap-2`}>
                      {getTypeIcon(institution.type)}
                      <span className="capitalize text-sm font-medium">{institution.type}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{institution.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {institution.description || 'No description available'}
                    </p>

                    {/* Location */}
                    {institution.address && (
                      <div className="flex items-center text-gray-500 text-sm mb-4">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span>
                          {institution.address.city}
                          {institution.address.state && `, ${institution.address.state}`}
                        </span>
                      </div>
                    )}

                    {/* View Details Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admissions/${institution.id}`);
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Comparison Modal */}
      {showComparisonModal && (
        <ComparisonModal
          institutions={institutions}
          onClose={() => setShowComparisonModal(false)}
          onCompare={handleCompare}
        />
      )}

      {/* Comparison View */}
      {showComparisonView && (
        <ComparisonView
          institutions={selectedForComparison}
          onClose={() => setShowComparisonView(false)}
        />
      )}
    </Layout>
  );
};

export default Admissions;

